import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AuthModal } from './AuthModal';
import { FormattedMessage, useIntl } from 'react-intl';
import { LanguageSelector } from './LanguageSelector';
import { Menu, X, User, LogIn, Plus, LogOut, Shield } from 'lucide-react';
import { useClickOutside, useGetData } from '../hooks';
import { useAuth } from '../providers/AuthProvider.tsx';
import { UserAvatar } from './UserAvatar.tsx';
import { COLLECTIONS, ROLES, ROUTES } from '../constants';

interface NavigationProps {
  theme?: 'light' | 'dark';
}

export function Navigation({ theme = 'light' }: NavigationProps) {
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isUserMenuMobileOpen, setIsUserMenuMobileOpen] = useState(false);

  const { formatMessage } = useIntl();
  const { user, logout } = useAuth();
  const { data: userData } = useGetData(user?.uid, COLLECTIONS.users);

  // Handle body scroll locking when modal is open
  useEffect(() => {
    if (isAuthModalOpen) {
      // Lock scrolling
      document.body.style.overflow = 'hidden';
    } else {
      // Restore scrolling
      document.body.style.overflow = '';
    }

    // Cleanup function to ensure scroll is restored when component unmounts
    return () => {
      document.body.style.overflow = '';
    };
  }, [isAuthModalOpen]);

  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const userMenuRefMobile = useRef<HTMLDivElement>(null);

  useClickOutside(mobileMenuRef, () => setIsMobileMenuOpen(false));
  useClickOutside(userMenuRef, () => setIsUserMenuOpen(false));
  useClickOutside(userMenuRefMobile, () => setIsUserMenuMobileOpen(false));

  const isAdmin = user?.role === ROLES.admin;

  const styles = {
    textColor: theme === 'light' ? 'text-white' : 'text-gray-800',
    hoverColor:
      theme === 'light' ? 'hover:text-blue-200' : 'hover:text-blue-600',
    buttonColor:
      theme === 'light'
        ? 'bg-white text-blue-600 hover:bg-blue-50'
        : 'bg-blue-600 text-white hover:bg-blue-700',
  };

  const UserMenuItems = ({ mobile = false }: { mobile?: boolean }) => (
    <>
      {user ? (
        <>
          <Link
            to={ROUTES.profile}
            className='flex items-center px-4 py-2 text-gray-700 hover:bg-gray-100'
            onClick={() => mobile && setIsUserMenuMobileOpen(false)}
          >
            <User className='w-4 h-4 mr-2' />
            <FormattedMessage id='nav.profile' defaultMessage='Профиль' />
          </Link>
          {isAdmin && (
            <Link
              to={ROUTES.admin}
              className='flex items-center px-4 py-2 text-gray-700 hover:bg-gray-100'
              onClick={() => mobile && setIsUserMenuMobileOpen(false)}
            >
              <Shield className='w-4 h-4 mr-2' />
              <FormattedMessage id='nav.admin' defaultMessage='Админ-панель' />
            </Link>
          )}
          <button
            onClick={() => logout()}
            className='flex items-center w-full px-4 py-2 text-gray-700 hover:bg-gray-100'
          >
            <LogOut className='w-4 h-4 mr-2' />
            <FormattedMessage id='nav.logOut' defaultMessage='Выйти' />
          </button>
        </>
      ) : (
        <button
          onClick={(e) => {
            if (!mobile) e.stopPropagation();
            setIsAuthModalOpen(true);
            mobile ? setIsUserMenuMobileOpen(false) : setIsUserMenuOpen(false);
          }}
          className='flex items-center w-full px-4 py-2 text-gray-700 hover:bg-gray-100'
        >
          <LogIn className='w-4 h-4 mr-2' />
          <FormattedMessage id='nav.login' defaultMessage='Войти' />
        </button>
      )}
    </>
  );

  return (
    <>
      <nav className='container mx-auto px-4 py-4'>
        <div className='flex justify-between items-center'>
          <Link
            to='/'
            className={`text-2xl mr-4 font-bold ${styles.textColor}`}
          >
            <FormattedMessage id='app.name' defaultMessage='NovoService' />
          </Link>

          {/* Desktop Menu */}
          <div className='hidden md:flex items-center space-x-5'>
            <Link
              to='/services'
              className={`${styles.textColor} ${styles.hoverColor}`}
            >
              <FormattedMessage id='nav.services' defaultMessage='Услуги' />
            </Link>
            <Link
              to='/faq'
              className={`${styles.textColor} ${styles.hoverColor}`}
            >
              <FormattedMessage
                id='nav.howItWorks'
                defaultMessage='Как это работает'
              />
            </Link>
            <Link
              to={ROUTES.addService}
              className={`${styles.buttonColor} px-4 py-2 rounded-lg`}
            >
              <FormattedMessage
                id='nav.postService'
                defaultMessage='Разместить услугу'
              />
            </Link>
            <LanguageSelector />

            <div className='relative' ref={userMenuRef}>
              <button
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                className={`flex items-center ${styles.textColor} ${styles.hoverColor}`}
              >
                {isAdmin && (
                  <div className='absolute -bottom-1 -right-1 bg-blue-600 rounded-full p-1 z-10'>
                    <Shield className='w-3 h-3 text-white' />
                  </div>
                )}
                {user ? (
                  <div className='relative w-8 h-8 rounded-full overflow-hidden'>
                    <UserAvatar
                      photoURL={userData?.photoUrl}
                      displayName={userData?.displayName}
                      email={userData?.email}
                      size='lg'
                    />
                  </div>
                ) : (
                  <User className='w-5 h-5' />
                )}
              </button>
              {isUserMenuOpen && (
                <div className='absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-2 z-50'>
                  <UserMenuItems />
                </div>
              )}
            </div>
          </div>

          {/* Mobile Controls */}
          <div className='flex items-center space-x-2 md:hidden'>
            <Link
              to={ROUTES.addService}
              className={`${styles.buttonColor} p-2 rounded-lg`}
              title={formatMessage({
                id: 'nav.postService',
                defaultMessage: 'Разместить услугу',
              })}
            >
              <Plus className='w-5 h-5' />
            </Link>
            <LanguageSelector />

            <div className='relative' ref={userMenuRefMobile}>
              <button
                onClick={() => setIsUserMenuMobileOpen(!isUserMenuMobileOpen)}
                className={`flex items-center ${styles.textColor} ${styles.hoverColor}`}
              >
                {isAdmin && (
                  <div className='absolute -bottom-1 -right-1 bg-blue-600 rounded-full p-1 z-10'>
                    <Shield className='w-3 h-3 text-white' />
                  </div>
                )}
                {user ? (
                  <div className='relative w-8 h-8 rounded-full overflow-hidden'>
                    <UserAvatar
                      photoURL={userData?.photoUrl}
                      displayName={userData?.displayName}
                      email={userData?.email}
                      size='lg'
                    />
                  </div>
                ) : (
                  <User className='w-5 h-5' />
                )}
              </button>
              {isUserMenuMobileOpen && (
                <div className='absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-2 z-50'>
                  <UserMenuItems mobile />
                </div>
              )}
            </div>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`p-2 ${styles.textColor}`}
            >
              {isMobileMenuOpen ? (
                <X className='w-6 h-6' />
              ) : (
                <Menu className='w-6 h-6' />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div
            ref={mobileMenuRef}
            className='md:hidden absolute top-16 left-0 right-0 bg-white shadow-lg z-50'
          >
            <div className='py-2'>
              <Link
                to='/services'
                className='block px-4 py-3 text-gray-700 hover:bg-gray-100'
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <FormattedMessage id='nav.services' defaultMessage='Услуги' />
              </Link>
              <Link
                to='/faq'
                className='block px-4 py-3 text-gray-700 hover:bg-gray-100'
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <FormattedMessage
                  id='nav.howItWorks'
                  defaultMessage='Как это работает'
                />
              </Link>
            </div>
          </div>
        )}
      </nav>

      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
      />
    </>
  );
}
